import React, { useState, useEffect, createContext, useContext, useRef } from 'react';
import {
  Box,
  Typography,
  Alert,
  Skeleton,
  Button,
} from '@mui/material';
import { ImageOutlined as ImageIcon, Add as AddIcon } from '@mui/icons-material';
import { Action, SignedUrlInfo } from '../types/action';
import { ActionCard } from './ActionCard';
import { ActionEditor } from './ActionEditor';
import { actionsService } from '../services/actionsService';
import { database } from '../firebase';
import { ref, onValue, off } from 'firebase/database';
import { useTranslation } from 'react-i18next';
import { useSentSchedule } from '../hooks/useSentSchedule';
import { useCurrentAction } from '../hooks/useCurrentAction';
import { isAwaitingApproval } from '../types/currentAction';
import { ReplacedOnTablet } from './SendActionDialog';
import { isSentScheduleActive } from '../types/sentSchedule';
import { stepPosition } from '../utils/sentSchedule';

// Context for sharing batch-fetched signed URLs with ActionCard components
interface SignedUrlsContextType {
  signedUrls: Record<string, SignedUrlInfo>;
  loading: boolean;
}

const SignedUrlsContext = createContext<SignedUrlsContextType>({
  signedUrls: {},
  loading: true,
});

export const useSignedUrls = () => useContext(SignedUrlsContext);

interface ActionsListProps {
  deviceId: string;
  deviceName?: string;
}

export const ActionsList: React.FC<ActionsListProps> = ({ deviceId, deviceName = '' }) => {
  const { t } = useTranslation();
  // A send that replaces what is on the child's screen, a running sent schedule (BA-85 D2) or
  // an action (P2), confirms like BA-55
  const { sentSchedule, loaded: sentScheduleLoaded } = useSentSchedule(deviceId);
  const { currentAction, loading: currentActionLoading } = useCurrentAction(deviceId);
  const [actions, setActions] = useState<Action[]>([]);
  let replaces: ReplacedOnTablet | null = null;
  if (sentSchedule && isSentScheduleActive(sentSchedule)) {
    replaces = { kind: 'schedule', name: sentSchedule.schedule?.name ?? '', ...stepPosition(sentSchedule), device: deviceName };
  } else if (currentAction) {
    replaces = {
      kind: 'action',
      name: actions.find((a) => a.id === currentAction.actionId)?.name ?? currentAction.actionId,
      device: deviceName,
      awaiting: isAwaitingApproval(currentAction),
    };
  }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState<Action | null>(null);

  // Batch-fetched signed URLs state
  const [signedUrls, setSignedUrls] = useState<Record<string, SignedUrlInfo>>({});
  const [urlsLoading, setUrlsLoading] = useState(true);
  const fetchedActionIdsRef = useRef<string>('');

  useEffect(() => {
    // Firebase real-time listener for actions
    const actionsRef = ref(database, `devices/${deviceId}/actions`);

    onValue(
      actionsRef,
      (snapshot) => {
        try {
          const data = snapshot.val();

          if (!data) {
            setActions([]);
            setLoading(false);
            return;
          }

          // Convert Firebase object to array of actions
          const actionsArray: Action[] = Object.entries(data).map(([id, actionData]: [string, any]) => ({
            id,
            name: actionData.name,
            description: actionData.description,
            pictureUrl: actionData.pictureUrl || null,
            thumbnailUrl: actionData.thumbnailUrl || null,
            soundUrl: actionData.soundUrl || null,
            defaults: actionData.defaults || null,
            createdAt: actionData.createdAt,
            updatedAt: actionData.updatedAt,
            updatedBy: actionData.updatedBy,
          }));

          // Sort by creation date (newest first)
          actionsArray.sort((a, b) => b.createdAt - a.createdAt);

          setActions(actionsArray);
          setLoading(false);
          setError(null);
        } catch (err) {
          console.error('Error processing actions data:', err);
          setError(t('actions.failedToLoadActions'));
          setLoading(false);
        }
      },
      (err) => {
        console.error('Firebase listener error:', err);
        setError(t('actions.failedToConnectFirebase'));
        setLoading(false);
      }
    );

    // Cleanup listener on unmount
    return () => {
      off(actionsRef);
    };
  }, [deviceId, t]);

  // Batch fetch signed URLs when actions change
  useEffect(() => {
    // Get action IDs that have pictures
    const actionIdsWithPictures = actions
      .filter(a => a.thumbnailUrl || a.pictureUrl)
      .map(a => a.id);

    // Create a key to detect if we need to refetch
    const actionIdsKey = actionIdsWithPictures.sort().join(',');

    // Skip if no actions with pictures or if we already fetched for these exact IDs
    if (actionIdsWithPictures.length === 0) {
      setUrlsLoading(false);
      return;
    }

    if (actionIdsKey === fetchedActionIdsRef.current) {
      return;
    }

    const fetchBatchUrls = async () => {
      setUrlsLoading(true);
      try {
        const response = await actionsService.getBatchSignedPictureUrls(
          deviceId,
          actionIdsWithPictures,
          true // prefer thumbnails
        );
        setSignedUrls(response.urls);
        fetchedActionIdsRef.current = actionIdsKey;
      } catch (err) {
        console.error('Error fetching batch picture URLs:', err);
        // On error, ActionCard will fall back to individual fetching
      } finally {
        setUrlsLoading(false);
      }
    };

    fetchBatchUrls();
  }, [actions, deviceId]);

  // Editor handlers
  const handleOpenEditor = (action?: Action) => {
    setSelectedAction(action || null);
    setEditorOpen(true);
  };

  const handleCloseEditor = () => {
    setEditorOpen(false);
    setSelectedAction(null);
  };

  const handleSaveAction = () => {
    // Firebase listener will automatically update the actions list
    // No manual refresh needed
  };

  // Loading skeleton
  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(1, 1fr)',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
            },
            gap: 3,
          }}
        >
          {[1, 2, 3].map((i) => (
            <Box key={i}>
              <Skeleton variant="rectangular" height={200} />
              <Skeleton variant="text" sx={{ mt: 1 }} />
              <Skeleton variant="text" width="60%" />
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  // Empty state
  if (actions.length === 0) {
    return (
      <>
        <Box
          sx={{
            p: 6,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
          }}
        >
          <ImageIcon sx={{ fontSize: 80, color: 'grey.300', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('actions.noActionsYet')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('actions.noActionsDescription')}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenEditor()}
          >
            {t('actions.addAction')}
          </Button>
        </Box>

        <ActionEditor
          open={editorOpen}
          onClose={handleCloseEditor}
          onSave={handleSaveAction}
          deviceId={deviceId}
          action={selectedAction}
        />
      </>
    );
  }

  // Actions grid
  return (
    <>
      <Box sx={{ p: 3 }}>
        {/* Header with Add button */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" color="text.primary">
            {t('actions.count', { count: actions.length })}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenEditor()}
          >
            {t('actions.addAction')}
          </Button>
        </Box>

        {/* Actions grid */}
        <SignedUrlsContext.Provider value={{ signedUrls, loading: urlsLoading }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(1, 1fr)',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
              },
              gap: 3,
            }}
          >
            {actions.map((action) => (
              <ActionCard
                key={action.id}
                action={action}
                onEdit={() => handleOpenEditor(action)}
                deviceId={deviceId}
                replaces={replaces}
                sendDisabled={!sentScheduleLoaded || currentActionLoading}
              />
            ))}
          </Box>
        </SignedUrlsContext.Provider>
      </Box>

      <ActionEditor
        open={editorOpen}
        onClose={handleCloseEditor}
        onSave={handleSaveAction}
        deviceId={deviceId}
        action={selectedAction}
      />
    </>
  );
};
